import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useContentAdmin } from '../../hooks/useEditableContent';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Sparkles } from 'lucide-react';
import ContentTable from './content/ContentTable';
import ContentFormDialog from './content/ContentFormDialog';
import VersionsDialog from './content/VersionsDialog';

const DEFAULT_FORM = {
  key: '',
  title: '',
  content: '',
  content_type: 'text',
  category: '',
  order: 0,
  is_published: true,
};

const ContentManager = () => {
  const { token } = useAuth();
  const {
    contents,
    loading,
    error,
    fetchAll,
    create,
    update,
    remove,
    getVersions,
    restoreVersion,
    seedDefaults,
  } = useContentAdmin(token);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingContent, setEditingContent] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsContentId, setVersionsContentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  useEffect(() => {
    if (token) fetchAll();
  }, [token, fetchAll]);

  const categories = [
    'all',
    ...new Set(contents.map((c) => c.category).filter(Boolean)),
  ];
  const filteredContents =
    selectedCategory === 'all'
      ? contents
      : contents.filter((c) => c.category === selectedCategory);

  const closeDialog = () => {
    setShowCreateDialog(false);
    setEditingContent(null);
  };

  const handleCreate = async () => {
    if (!formData.key || !formData.title || !formData.content) {
      toast.error('Key, title, and content are required');
      return;
    }
    setSaving(true);
    try {
      await create(formData);
      closeDialog();
      setFormData(DEFAULT_FORM);
      toast.success('Content created');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingContent) return;
    setSaving(true);
    try {
      await update(editingContent.id, formData);
      closeDialog();
      toast.success('Content updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await remove(id);
      toast.success('Content deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleViewVersions = async (contentId) => {
    try {
      const versionList = await getVersions(contentId);
      setVersions(versionList);
      setVersionsContentId(contentId);
      setShowVersionsDialog(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRestore = async (versionId) => {
    if (!window.confirm('Restore this version? Current content will be overwritten.'))
      return;
    try {
      await restoreVersion(versionsContentId, versionId);
      setShowVersionsDialog(false);
      toast.success('Version restored');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const result = await seedDefaults();
      toast.success(
        `Created ${result.created} content blocks, ${result.skipped} already existed`
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openEditDialog = (content) => {
    setFormData({
      key: content.key,
      title: content.title,
      content: content.content,
      content_type: content.content_type,
      category: content.category || '',
      order: content.order,
      is_published: content.is_published,
    });
    setEditingContent(content);
  };

  const openCreateDialog = () => {
    setFormData({
      ...DEFAULT_FORM,
      category: selectedCategory === 'all' ? '' : selectedCategory,
    });
    setShowCreateDialog(true);
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-manager">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Content Manager
              </CardTitle>
              <CardDescription>
                Manage editable content blocks displayed throughout the site
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleSeedDefaults}>
                <Sparkles className="w-4 h-4 mr-2" />
                Seed Defaults
              </Button>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Content
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <Label>Filter by Category:</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ContentTable
            loading={loading}
            contents={filteredContents}
            onEdit={openEditDialog}
            onViewVersions={handleViewVersions}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <ContentFormDialog
        open={showCreateDialog || !!editingContent}
        isEdit={!!editingContent}
        formData={formData}
        setFormData={setFormData}
        saving={saving}
        onSubmit={editingContent ? handleUpdate : handleCreate}
        onClose={closeDialog}
      />

      <VersionsDialog
        open={showVersionsDialog}
        onOpenChange={setShowVersionsDialog}
        versions={versions}
        onRestore={handleRestore}
      />
    </div>
  );
};

export default ContentManager;
